import {
  CarAxleTypeLabels,
  CatalogAttribute,
  TireSeason,
  TireSeasonLabels,
  VehicleCatalogNodeKind,
  type CarAxleType,
  type CarFromLicensePlateRecordWithOwners,
  type CarWheelSetCatalogMappingRecord,
  type CarWheelSetCatalogRecord,
  type CatalogWheelPackageRecord
} from '@navio/nsdk';
import {
  Collapsible,
  CollapsibleContent,
  NoddiButton,
  NoddiCard,
  NoddiDropDownActions,
  NoddiStaticTable,
  type Action,
  type NoddiColumnDef
} from 'noddi-ui-web';
import { catalogSpecificationValueText, formatAxlePressure } from 'noddi-util';
import { useState, type ReactNode } from 'react';
import { NavioTireSeasonChip } from '../CarWheel/NavioTireSeasonChip';
import { CreateWheelSetSheet } from '../WheelSets/CreateWheelSetSheet';
import { CarWheelPackagesTrigger } from './CarWheelPackagesTrigger';
import { carWheelSetServiceText } from './carWheelSetServiceText';
import { CarWheelSetMatchedBadge, CarWheelSetSpecificationsAction } from './CarWheelSetSpecificationsAction';
import { CatalogFasteningCard } from './CatalogFasteningCard';
import { MapWheelSetPackageSheet } from './MapWheelSetPackageSheet';
import { specificationsReviewAction } from './specificationsReview';
import { UpdateCatalogDataButton } from './UpdateCatalogDataButton';
import { useUpdateCarCatalogData } from './useUpdateCatalogData';

/** The seasons a car always answers for, even before a wheel set is registered. */
const BASE_SEASONS: TireSeason[] = [TireSeason.SUMMER, TireSeason.WINTER];

type WheelSet = CarWheelSetCatalogRecord;
type AxleRow = {
  key: CarAxleType;
  axle: string;
  size: string | null;
  pressure: string | null;
  torque: string;
};

type Props = {
  car: CarFromLicensePlateRecordWithOwners;
  onEditCar: () => void;
  /** Brand, model and where they came from. Not the edit controls. */
  renderSeasonSummary?: (season: TireSeason) => ReactNode;
  /**
   * Edits this app adds for one season. Combined with "Review specifications" into one Edit menu.
   * Absent, the shared card keeps the review button on its own, as admin does.
   */
  seasonEditActions?: (season: TireSeason) => Action[];
  /** Tread depth, photos and provenance for one season; only apps that load them pass this. */
  renderSeasonDetails?: (season: TireSeason) => ReactNode;
};

/**
 * Everything about a car's tires, one section per season: the registered set, its axle sizes and
 * pressures, the specifications it is mapped to, and whatever tread depth and photos the app can
 * supply. One card, because a season answered in two cards is read as two different subjects.
 */
export const CarTiresCard = ({
  car,
  onEditCar,
  renderSeasonSummary,
  seasonEditActions,
  renderSeasonDetails
}: Props) => {
  const [createOpen, setCreateOpen] = useState(false);
  const [mappingSet, setMappingSet] = useState<WheelSet | null>(null);
  const { updateData, isPending: isUpdatingData } = useUpdateCarCatalogData();
  const wheelSets = car.wheelSetCatalogDetails ?? [];
  const availableSeasons = Object.values(TireSeason).filter(
    (season) =>
      !wheelSets.some(
        (set) => set.type.value === season && (set.axles ?? []).some((axle) => axle.tireSpecificationStr != null)
      )
  );
  const trims =
    car.matchingDetails?.catalogNodes.filter((node) => node.kind === VehicleCatalogNodeKind.MODIFICATION) ?? [];
  const packages = trims.flatMap((node) =>
    node.wheelPackages.map((pkg) => ({
      package: pkg,
      label: `${node.name} · ${pkg.axles
        .map((axle) => axle.tireSpecification)
        .filter(Boolean)
        .join(' / ')} · #${pkg.id}`
    }))
  );
  const seasons = [
    ...BASE_SEASONS,
    ...wheelSets.map((set) => set.type.value).filter((season) => !BASE_SEASONS.includes(season))
  ];
  const createDisabledReason =
    car.topology.expectedWheelCount !== 4
      ? 'Wheel set registration currently supports four-wheel vehicles.'
      : availableSeasons.length === 0
        ? 'All seasons already have registered wheel sets.'
        : undefined;

  return (
    <>
      <NoddiCard
        title='Tires'
        icon='WheelAngle'
        iconTile
        description='Registered tire sizes automatically match compatible wheel packages. Review specifications only to choose a different package.'
        headerNode={
          <NoddiButton
            size='small'
            variant='outline'
            startIcon='Plus'
            onClick={() => setCreateOpen(true)}
            disabledReason={createDisabledReason}
          >
            Create wheel set
          </NoddiButton>
        }
        contentClassName='flex flex-col divide-y divide-border'
      >
        {packages.length === 0 && (
          <NoSpecificationsNotice
            onEditCar={onEditCar}
            onUpdateData={() => void updateData(car.id)}
            isUpdatingData={isUpdatingData}
            canUpdate={car.matchingDetails?.targetNodeId != null}
          />
        )}
        {seasons.map((season) => (
          <SeasonSection
            key={season}
            season={season}
            wheelSet={wheelSets.find((set) => set.type.value === season)}
            canMap={packages.length > 0}
            onMap={setMappingSet}
            summary={renderSeasonSummary?.(season)}
            editActions={seasonEditActions?.(season) ?? []}
            details={renderSeasonDetails?.(season)}
          />
        ))}
        {trims.length > 0 && packages.length > 0 && (
          <div className='flex flex-col gap-4 pt-6'>
            {trims.map((trim) => (
              <Collapsible key={trim.id}>
                <CarWheelPackagesTrigger node={trim} />
                <CollapsibleContent className='pt-4'>
                  <CatalogFasteningCard
                    packages={trim.wheelPackages}
                    fastening={{
                      fastenerType: trim.fastenerType,
                      threadSize: trim.threadSize,
                      wheelTorques: trim.specifications.filter(
                        (spec) => spec.attribute === CatalogAttribute.WHEEL_TORQUE
                      )
                    }}
                  />
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>
        )}
      </NoddiCard>
      {createOpen && (
        <CreateWheelSetSheet
          carId={car.id}
          licensePlateNumber={car.licensePlateNumber}
          availableSeasons={availableSeasons}
          onClose={() => setCreateOpen(false)}
        />
      )}
      {mappingSet && (
        <MapWheelSetPackageSheet
          carId={car.id}
          wheelSet={mappingSet}
          packages={packages}
          onClose={() => setMappingSet(null)}
        />
      )}
    </>
  );
};

/**
 * The one reason no specifications apply, with the two actions that resolve it: pull the trim's
 * published data, or correct the trim the car is matched to. Stated once for the whole card, so a
 * season row never repeats it.
 */
const NoSpecificationsNotice = ({
  onEditCar,
  onUpdateData,
  isUpdatingData,
  canUpdate
}: {
  onEditCar: () => void;
  onUpdateData: () => void;
  isUpdatingData: boolean;
  canUpdate: boolean;
}) => (
  <div className='flex flex-col items-start gap-3 pb-6'>
    <p className='text-sm text-muted-foreground'>
      No specifications stored for this car’s trim, so a wheel set cannot be mapped yet.
    </p>
    <div className='flex flex-wrap items-center gap-2'>
      <UpdateCatalogDataButton
        onClick={onUpdateData}
        isPending={isUpdatingData}
        disabledReason={canUpdate ? undefined : 'This car is not linked to the catalog yet'}
      />
      <NoddiButton size='small' variant='outline' startIcon='Edit' onClick={onEditCar}>
        Edit car information
      </NoddiButton>
    </div>
  </div>
);

/** The pressure the mapped specifications give one axle, or nothing when none is mapped. */
const mappedAxlePressure = (
  mapped: CatalogWheelPackageRecord | null | undefined,
  axleType: CarAxleType
): string | null => {
  const axle = mapped?.axles.find((entry) => entry.axleType === axleType);
  return axle ? (formatAxlePressure(axle) ?? null) : null;
};

const axleColumns: NoddiColumnDef<AxleRow>[] = [
  { header: 'Axle', accessorKey: 'axle' },
  { header: 'Size', accessorKey: 'size' },
  { header: 'Tire pressure', accessorKey: 'pressure' },
  { header: 'Wheel nut torque', accessorKey: 'torque' }
];

/** A reviewed package takes precedence over automatically applicable specifications. */
const axleRows = (wheelSet: WheelSet, mapping: CarWheelSetCatalogMappingRecord | null | undefined): AxleRow[] => {
  const mapped = mapping?.package;
  const recommended = wheelSet.recommendedServiceSpecifications;
  const torque = mapped
    ? [...new Set((mapping?.wheelTorques ?? []).map(catalogSpecificationValueText))].join('; ')
    : carWheelSetServiceText(recommended?.wheelTorques ?? []);
  return (wheelSet.axles ?? []).map((axle) => ({
    key: axle.type,
    axle: CarAxleTypeLabels[axle.type],
    size: axle.tireSpecificationStr ?? null,
    pressure:
      (mapped
        ? mappedAxlePressure(mapped, axle.type)
        : carWheelSetServiceText(
            recommended?.tirePressures.filter((pressure) => pressure.axleType === axle.type) ?? []
          )) ?? 'Not reported',
    torque: torque == null || torque.length === 0 ? 'Not reported' : torque
  }));
};

type SeasonSectionProps = {
  season: TireSeason;
  wheelSet: WheelSet | undefined;
  canMap: boolean;
  onMap: (wheelSet: WheelSet) => void;
  summary: ReactNode;
  editActions: Action[];
  details: ReactNode;
};

const SeasonSection = ({ season, wheelSet, canMap, onMap, summary, editActions, details }: SeasonSectionProps) => {
  const mapping = wheelSet?.catalogMapping?.package ? wheelSet.catalogMapping : wheelSet?.automaticPackageMapping;
  const mapped = mapping?.package;
  const rows = wheelSet ? axleRows(wheelSet, mapping) : [];
  // Partner sends its own edits, so review joins that menu. Admin has review alone and keeps the button.
  const useMenu = editActions.length > 0;
  const menuActions = wheelSet ? [...editActions, specificationsReviewAction(wheelSet, canMap, onMap)] : editActions;

  return (
    <section className='flex flex-col gap-4 py-6 first:pt-0 last:pb-0'>
      <div className='flex flex-wrap items-center gap-x-3 gap-y-2'>
        <NavioTireSeasonChip season={season} size='sm' />
        {summary != null && <div className='flex min-w-0 flex-1 flex-wrap items-center gap-2'>{summary}</div>}
        <div className='ml-auto flex shrink-0 flex-wrap items-center gap-2'>
          {useMenu && wheelSet && <CarWheelSetMatchedBadge wheelSet={wheelSet} />}
          {useMenu ? (
            <NoddiDropDownActions
              title={
                <>
                  Edit
                  <span className='sr-only'> {TireSeasonLabels[season].toLowerCase()} tires</span>
                </>
              }
              triggerIcon='Edit'
              triggerSize='sm'
              className='shrink-0'
              actions={menuActions}
            />
          ) : (
            wheelSet && <CarWheelSetSpecificationsAction wheelSet={wheelSet} canMap={canMap} onReview={onMap} />
          )}
        </div>
      </div>
      {wheelSet ? (
        <NoddiStaticTable
          columns={axleColumns}
          data={rows}
          getRowId={(row) => row.key}
          wrapInCard={false}
          heightMode='content'
          enableSearch={false}
          hideCopyRowId
          hideColumnToggle
          hideRowCount
        />
      ) : (
        <p className='text-sm text-muted-foreground'>No wheel set registered for this season.</p>
      )}
      {wheelSet?.catalogMapping?.optionId != null && !mapped && (
        <p className='text-sm text-muted-foreground'>
          The mapped specifications no longer apply. Review the car match and axle sizes, then map the set again.
        </p>
      )}
      {details}
    </section>
  );
};
